import { useEffect, useRef, useCallback, useState } from 'react';
import { ChatColorsContext } from '@/contexts/ChatColorsContext';
import { useChatColorsStore } from '@/hooks/useChatColorsStore';
import { useChatFlow } from '@/hooks/useChatFlow';
import { useLeads } from '@/hooks/useLeads';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import ChatInput from './ChatInput';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const { colors } = useChatColorsStore();
  const { addLead } = useLeads();
  const bottomRef = useRef<HTMLDivElement>(null);

  const handleLeadCaptured = useCallback(
    (name: string, email: string, phone: string, interest: 'Atacado' | 'Varejo') => {
      addLead({ name, email, phone, interest });
    },
    [addLead]
  );

  const {
    messages,
    isTyping,
    inputDisabled,
    startConversation,
    handleUserTextInput,
    handleFlowButton,
  } = useChatFlow(handleLeadCaptured);

  useEffect(() => {
    startConversation();
  }, [startConversation]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [messages, isTyping, isOpen]);

  return (
    <ChatColorsContext.Provider value={colors}>
      <div
        className="fixed bottom-24 right-4 sm:right-6 z-50 flex flex-col rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-2xl"
        style={{
          width: 'min(380px, calc(100vw - 32px))',
          height: 'min(580px, calc(100vh - 120px))',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
          transformOrigin: 'bottom right',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
        }}
      >
        <ChatHeader onClose={() => setIsOpen(false)} />

        <div
          className="flex-1 overflow-y-auto px-4 pt-4 pb-2"
          style={{
            backgroundImage: 'radial-gradient(circle, #fce7f3 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            backgroundColor: '#FDFAFC',
          }}
        >
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onFlowButtonClick={handleFlowButton}
            />
          ))}

          {isTyping && <TypingIndicator />}

          <div ref={bottomRef} />
        </div>

        <ChatInput disabled={inputDisabled} onSend={handleUserTextInput} />
      </div>

      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50 w-16 h-16 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer overflow-hidden border-4 border-white"
        style={{ backgroundColor: colors.primary }}
        aria-label={isOpen ? 'Fechar chat' : 'Abrir chat'}
      >
        <span
          className="absolute inset-0 flex items-center justify-center transition-all duration-300"
          style={{
            opacity: isOpen ? 0 : 1,
            transform: isOpen ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
          }}
        >
          <i className="ri-question-line text-white text-3xl" />
        </span>

        <span
          className="absolute inset-0 flex items-center justify-center transition-all duration-300"
          style={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
          }}
        >
          <i className="ri-close-line text-white text-2xl" />
        </span>
      </button>
    </ChatColorsContext.Provider>
  );
}